import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AlertService } from 'src/app/services/alert.service';
import { ComproCabeProveIaService } from 'src/app/services/compro-cabe-prove-ia.service';
import { LimitesFacturasService } from 'src/app/services/limites-facturas.service';
import { EnvioMailService } from 'src/app/services/envio-mail.service';
import { ConfigGeneralService } from 'src/app/services/config-general.service';
import { ConfigGeneralDto } from 'src/app/models/config-general.dto';
import { SelectOption } from 'src/app/shared/autocomplete-con-filtros/autocomplete-con-filtros.component';
import { CentrosCostoService } from 'src/app/services/centros-costo.service';
import { CentrosCostoDto } from 'src/app/models/centros-costo.dto';

export interface ArchivoConEstado {
  file: File;
  procesando: boolean;
  completado: boolean;
  error: boolean;
  errorMessage?: string;
  emailEnviado?: boolean;
}

@Component({
  selector: 'app-modal-subir-pdf',
  templateUrl: './modal-subir-pdf.component.html',
  styleUrl: './modal-subir-pdf.component.scss'
})
export class ModalSubirPdfComponent implements OnInit {
  @Output() cerrar = new EventEmitter<void>();
  @Output() facturasSubidas = new EventEmitter<void>();
  @Output() mostrarAlerta = new EventEmitter<{
    type: 'success' | 'error' | 'warning' | 'info',
    title: string,
    message: string,
    confirmText?: string,
    showCancel?: boolean,
    onConfirm?: () => void,
    onCancel?: () => void
  }>();

  archivos: ArchivoConEstado[] = [];
  isDragOver = false;
  procesandoArchivos = false;
  progreso = 0;
  archivosCompletados = 0;
  archivosConError = 0;
  archivosConErrorSinEnviar=0;
  habilitarModoCarga = false;
  modoCarga = 'conceptos';
  habilitarCencoMasivo = false;
  cencoSeleccionado = '';
  cantMaximaArchivos = 2;
  private audioContext: AudioContext | null = null;

    // Propiedades para tipos de comprobantes
    cencos: any[] = [];
    selectOptionsCencos: SelectOption[] = [];
  
  // Propiedades para manejar reprocesamiento
  get archivosConErrorLista(): ArchivoConEstado[] {
    return this.archivos.filter(archivo => archivo.error);
  }
  

  get listaArchivosConErrorSinEnviar(): ArchivoConEstado[] {
    return this.archivosConErrorLista.filter(archivo => !archivo.emailEnviado);
  }


  get tieneArchivosConErrorSinEnviar(): boolean {
    return this.listaArchivosConErrorSinEnviar.length > 0;
  }
  

  get archivosExitosos(): ArchivoConEstado[] {
    return this.archivos.filter(archivo => archivo.completado);
  }
  
  get tieneArchivosConError(): boolean {
    return this.archivosConErrorLista.length > 0;
  }
  
  get limiteArchivosAlcanzado(): boolean {
    return this.archivos.length >= this.cantMaximaArchivos;
  }
  
  get archivosDisponibles(): number {
    return this.cantMaximaArchivos - this.archivos.length;
  }

  constructor(
    private comproCabeProveIaService: ComproCabeProveIaService,
    public alertService: AlertService,
    private limitesFacturasService: LimitesFacturasService,
    private envioMailService: EnvioMailService,
    private configGeneralService: ConfigGeneralService,
    private centrosCostoService: CentrosCostoService
  ) {}

  ngOnInit(): void {
    this.obtenerLimites();
    this.obtenerHabilitarModoCarga();
    this.obtenerModoCarga();
    this.obtenerHabilitarCencoMasivo();
    this.cargarCencos();
  }

  cargarCencos(): void {
    this.centrosCostoService.getAll().subscribe((res: CentrosCostoDto[]) => {
      if (res && res.length > 0) {
      this.selectOptionsCencos = res.map(cenco => ({
          value: cenco.codi_cenco ?? '',
          label: cenco.descrip_cenco ?? ''
        }));
      } else {
        this.selectOptionsCencos = [];
      }
    });
  }

  onCencoChange(event: string) {
    if (event) {
      this.cencoSeleccionado = event;
    } else {
      this.cencoSeleccionado = '';
    }
  }

  obtenerLimites(): void {
    this.configGeneralService.getByConfig('CONLINE_CANT_MAXIMA_PDF_DOC_IA').subscribe((res:ConfigGeneralDto[]) => {
      if (res && res[0]) {
        this.cantMaximaArchivos = Number(res[0].VALOR_CONFIG || 2);
      }
    });
  }

  obtenerHabilitarModoCarga(): void {
    this.configGeneralService.getByConfig('CONLINE_HABILITAR_MODO_DE_CARGA_ARCHIVO_IA').subscribe((res:ConfigGeneralDto[]) => {
      if (res && res[0]) {
        this.habilitarModoCarga = res[0].VALOR_CONFIG === 'S';
      }
    });
  }

  obtenerHabilitarCencoMasivo(): void {
    this.configGeneralService.getByConfig('ASIGNAR_CENCO_MASIVO_IA').subscribe((res:ConfigGeneralDto[]) => {
      if (res && res[0]) {
        this.habilitarCencoMasivo = res[0].VALOR_CONFIG === 'S';
      }
    });
  }

  obtenerModoCarga(): void {
    this.configGeneralService.getByConfig('CONLINE_MODO_CARGA_DEFAULT_PDF_IA').subscribe((res:ConfigGeneralDto[]) => {
      if (res && res[0]) {
        this.modoCarga = (res[0].VALOR_CONFIG == 'conceptos' || res[0].VALOR_CONFIG == 'articulos') ? res[0].VALOR_CONFIG : 'conceptos';
      }
    });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    
    const archivos = Array.from(event.dataTransfer?.files || []);
    this.procesarNuevosArchivos(archivos);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const archivos = Array.from(input.files);
      this.procesarNuevosArchivos(archivos);
    }
  }

  procesarNuevosArchivos(archivos: File[]) {
    const archivosPdf = archivos.filter(archivo => 
      archivo.type === 'application/pdf' || archivo.name.toLowerCase().endsWith('.pdf')
    );
    
    // Verificar límite máximo de archivos
    const archivosActuales = this.archivos.length;
    const archivosDisponibles = this.cantMaximaArchivos - archivosActuales;
    
    if (archivosDisponibles <= 0) {
      this.mostrarAlerta.emit({
        type: 'warning',
        title: 'Límite de Archivos Alcanzado',
        message: `Se ha alcanzado el límite máximo de ${this.cantMaximaArchivos} archivos PDF. Elimine algunos archivos antes de agregar nuevos.`,
        confirmText: 'Aceptar',
        showCancel: false
      });
      return;
    }
    
    // Limitar la cantidad de archivos nuevos al espacio disponible
    const archivosParaProcesar = archivosPdf.slice(0, archivosDisponibles);
    
    if (archivosPdf.length > archivosDisponibles) {
      this.mostrarAlerta.emit({
        type: 'warning',
        title: 'Límite de Archivos',
        message: `Solo se pueden agregar ${archivosDisponibles} archivos más. Se procesarán los primeros ${archivosDisponibles} archivos seleccionados.`,
        confirmText: 'Aceptar',
        showCancel: false
      });
    }
    
    // Filtrar archivos duplicados
    const archivosNuevos = archivosParaProcesar.filter(nuevoArchivo => {
      const existe = this.archivos.some(archivoExistente => 
        archivoExistente.file.name === nuevoArchivo.name && 
        archivoExistente.file.size === nuevoArchivo.size
      );
      
      if (existe) {
        this.mostrarAlerta.emit({
          type: 'warning',
          title: 'Archivo Duplicado',
          message: `El archivo "${nuevoArchivo.name}" ya está en la lista.`,
          confirmText: 'Aceptar',
          showCancel: false
        });
      }
      
      return !existe;
    });
    
    const archivosConEstado: ArchivoConEstado[] = archivosNuevos.map(archivo => ({
      file: archivo,
      procesando: false,
      completado: false,
      error: false
    }));
    
    this.archivos = [...this.archivos, ...archivosConEstado];
  }

  enviarArchivosConError() {
    this.procesandoArchivos = true;
    const archivosConError = this.listaArchivosConErrorSinEnviar;
    for (const archivo of archivosConError) {
      this.enviarArchivo(archivo, archivosConError.indexOf(archivo));
    }
    this.procesandoArchivos = false;
  }

  enviarArchivo(archivo: ArchivoConEstado, index: number) {
    firstValueFrom(this.envioMailService.enviarArchivoConError(archivo)) .then((response: any) => {
      if (response.success) {
        archivo.emailEnviado = true;
        this.archivos[index] = archivo;
        this.alertService.showSuccessToast('Archivo enviado correctamente', 'Archivo enviado correctamente');
      } else {
        archivo.emailEnviado = false;
        this.archivos[index] = archivo;
        this.alertService.showErrorToast('Error al enviar archivo', 'Error al enviar archivo');
      } 
    }).catch((error: any) => {
      archivo.emailEnviado = false;
      this.archivos[index] = archivo;
      this.alertService.showErrorToast('Error al enviar archivo', 'Error al enviar archivo');
    });
  }

  eliminarArchivo(index: number) {
    this.archivos.splice(index, 1);
  }

  /**
   * Verifica si se puede procesar archivos según los límites y demo
   */
  private async verificarLimitesAntesDeProcesar(): Promise<boolean> {
    // try {
    //   // Verificar si el demo está vencido
    //   if (this.demoFacturasService.isDemoVencido()) {
    //     this.mostrarAlerta.emit({
    //       type: 'error',
    //       title: 'Período de Demostración Vencido',
    //       message: 'El período de demostración de facturas IA ha vencido. Contacta con info@rpsistemas.com.ar para más información sobre los planes disponibles.',
    //       confirmText: 'Aceptar',
    //       showCancel: false
    //     });
    //     return false;
    //   }

    //   // Verificar límites antes de procesar de forma asíncrona
    //   const limitesState = await this.limitesFacturasService.verificarLimitesAsync();
      
    //   if (limitesState.limiteAlcanzado) {
    //     this.mostrarAlerta.emit({
    //       type: 'error',
    //       title: 'Límite Alcanzado',
    //       message: `No se pueden procesar más facturas. ${limitesState.mensaje}`,
    //       confirmText: 'Aceptar',
    //       showCancel: false
    //     });
    //     return false;
    //   }
      
    //   // Verificar si hay espacio suficiente para los archivos que se van a procesar
    //   const archivosAProcesar = this.archivos.length;
    //   const espacioDisponible = limitesState.limite - limitesState.cantidadIngresadas;
      
    //   if (archivosAProcesar > espacioDisponible) {
    //     this.mostrarAlerta.emit({
    //       type: 'error',
    //       title: 'Límite Insuficiente',
    //       message: `Solo se pueden procesar ${espacioDisponible} facturas más. Tienes ${archivosAProcesar} archivos seleccionados.`,
    //       confirmText: 'Aceptar',
    //       showCancel: false
    //     });
    //     return false;
    //   }
      
    //   return true;
    // } catch (error) {
    //   console.error('Error al verificar límites:', error);
    //   this.mostrarAlerta.emit({
    //     type: 'error',
    //     title: 'Error de Verificación',
    //     message: 'Error al verificar límites de facturas. Intente nuevamente.',
    //     confirmText: 'Aceptar',
    //     showCancel: false
    //   });
    //   return false;
    // }
    return true;
  }

  async procesarArchivos() {
    if (this.archivos.length === 0) return;

    // Verificar si hay archivos duplicados antes de procesar
    const archivosUnicos = new Set<string>();
    const archivosDuplicados: string[] = [];
    
    this.archivos.forEach(archivo => {
      const clave = `${archivo.file.name}-${archivo.file.size}`;
      if (archivosUnicos.has(clave)) {
        archivosDuplicados.push(archivo.file.name);
      } else {
        archivosUnicos.add(clave);
      }
    });
    
    if (archivosDuplicados.length > 0) {
      this.mostrarAlerta.emit({
        type: 'error',
        title: 'Archivos Duplicados',
        message: `Se encontraron archivos duplicados: ${archivosDuplicados.join(', ')}. Por favor, elimine los duplicados antes de procesar.`,
        confirmText: 'Aceptar',
        showCancel: false
      });
      return;
    }

    // Verificar límites antes de procesar
    const puedeProcesar = await this.verificarLimitesAntesDeProcesar();
    if (!puedeProcesar) {
      return;
    }

    this.procesandoArchivos = true;
    this.progreso = 0;
    this.archivosCompletados = 0;
    this.archivosConError = 0;
    
    try {
      for (let i = 0; i < this.archivos.length; i++) {
        const archivo = this.archivos[i];
        
        // Solo procesar archivos que no han sido procesados exitosamente
        if (archivo.completado) {
          continue;
        }
        
        archivo.procesando = true;

        try {
          // Opción 1: Enviar con FormData (recomendado)
          const response = await firstValueFrom(this.comproCabeProveIaService.postComproCabeProve(archivo.file, this.modoCarga, this.cencoSeleccionado));
          
          // Opción 2: Enviar con Base64 (alternativa)
          // const response = await firstValueFrom(this.comproCabeProveService.postComproCabeProveBase64(archivo.file));
          
          archivo.procesando = false;
          archivo.completado = true;
          archivo.error = false; // Limpiar error si se procesó exitosamente
          archivo.errorMessage = undefined;
          this.archivosCompletados++;
          
          // Reproducir sonido de éxito
          this.reproducirSonido('success');
          
          // Actualizar límites después de cada factura procesada exitosamente
          this.limitesFacturasService.verificarLimitesAsync();
          
        } catch (error) {
          console.error('Error al procesar archivo:', error);
          archivo.procesando = false;
          archivo.error = true;
          this.archivosConError++;
          
          // Obtener mensaje de error
          let errorMessage = 'Error desconocido';
          if (error && typeof error === 'object') {
            const errorObj = error as any;
            // Buscar el mensaje en diferentes niveles de la estructura del error
            if (errorObj.originalError && errorObj.originalError.error && errorObj.originalError.error.message) {
              errorMessage = errorObj.originalError.error.message;
            } else if (errorObj.error && errorObj.error.message) {
              errorMessage = errorObj.error.message;
            } else if (errorObj.message) {
              errorMessage = errorObj.message;
            }
          }
          archivo.errorMessage = errorMessage;
          
          // Reproducir sonido de error
          this.reproducirSonido('error');
        }
        
        this.progreso = ((i + 1) / this.archivos.length) * 100;
      }
      
      // Esperar un momento para mostrar el progreso completo
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mostrar resumen final
      this.mostrarResumenProcesamiento();
      
      this.facturasSubidas.emit();
    } catch (error) {
      console.error('Error al procesar archivos:', error);
    } finally {
      this.procesandoArchivos = false;
    }
  }

  /**
   * Reprocesa solo los archivos que fallaron
   */
  async reprocesarArchivosFallidos() {
    if (!this.tieneArchivosConError) {
      this.mostrarAlerta.emit({
        type: 'info',
        title: 'Sin Archivos para Reprocesar',
        message: 'No hay archivos con error para reprocesar.',
        confirmText: 'Aceptar',
        showCancel: false
      });
      return;
    }

    // Verificar límites antes de reprocesar
    const puedeProcesar = await this.verificarLimitesAntesDeProcesar();
    if (!puedeProcesar) {
      return;
    }

    this.procesandoArchivos = true;
    this.progreso = 0;
    this.archivosCompletados = 0;
    this.archivosConError = 0;
    
    const archivosConError = this.archivosConErrorLista;
    
    try {
      for (let i = 0; i < archivosConError.length; i++) {
        const archivo = archivosConError[i];
        archivo.procesando = true;
        archivo.error = false; // Limpiar estado de error
        archivo.errorMessage = undefined;

        try {
          const response = await firstValueFrom(this.comproCabeProveIaService.postComproCabeProve(archivo.file, this.modoCarga, this.cencoSeleccionado));
          
          archivo.procesando = false;
          archivo.completado = true;
          this.archivosCompletados++;
          
          // Reproducir sonido de éxito
          this.reproducirSonido('success');
          
          // Actualizar límites después de cada factura procesada exitosamente
          this.limitesFacturasService.verificarLimitesAsync();
          
        } catch (error) {
          console.error('Error al reprocesar archivo:', error);
          archivo.procesando = false;
          archivo.error = true;
          this.archivosConError++;
          
          // Obtener mensaje de error
          let errorMessage = 'Error desconocido';
          if (error && typeof error === 'object') {
            const errorObj = error as any;
            // Buscar el mensaje en diferentes niveles de la estructura del error
            if (errorObj.originalError && errorObj.originalError.error && errorObj.originalError.error.message) {
              errorMessage = errorObj.originalError.error.message;
            } else if (errorObj.error && errorObj.error.message) {
              errorMessage = errorObj.error.message;
            } else if (errorObj.message) {
              errorMessage = errorObj.message;
            }
          }
          archivo.errorMessage = errorMessage;
          
          // Reproducir sonido de error
          this.reproducirSonido('error');
        }
        
        this.progreso = ((i + 1) / archivosConError.length) * 100;
      }
      
      // Esperar un momento para mostrar el progreso completo
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mostrar resumen final
      this.mostrarResumenProcesamiento();
      
      this.facturasSubidas.emit();
    } catch (error) {
      console.error('Error al reprocesar archivos:', error);
    } finally {
      this.procesandoArchivos = false;
    }
  }

  cerrarModal() {
    if (!this.procesandoArchivos) {
      this.limpiarEstado();
      this.cerrar.emit();
    }
  }

  /**
   * Limpia el estado del modal
   */
  private limpiarEstado(): void {
    this.archivos = [];
    this.procesandoArchivos = false;
    this.progreso = 0;
    this.archivosCompletados = 0;
    this.archivosConError = 0;
    this.isDragOver = false;
  }

  formatearTamano(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Reproduce un sonido de notificación
   */
  private reproducirSonido(tipo: 'success' | 'error'): void {
    try {
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }

      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Configurar frecuencia y duración según el tipo
      if (tipo === 'success') {
        oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime);
        oscillator.frequency.setValueAtTime(1000, this.audioContext.currentTime + 0.1);
      } else {
        oscillator.frequency.setValueAtTime(400, this.audioContext.currentTime);
        oscillator.frequency.setValueAtTime(300, this.audioContext.currentTime + 0.1);
      }

      gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.2);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.2);
    } catch (error) {
      console.warn('No se pudo reproducir el sonido:', error);
    }
  }

  private mostrarResumenProcesamiento(): void {
    const total = this.archivos.length;
    const exitosos = this.archivosCompletados;
    const errores = this.archivosConError;


    if (errores === 0) {
      const mensaje = `Todos los archivos (${exitosos}) se procesaron correctamente.`;
      
      // Limpiar estado antes de cerrar
      this.limpiarEstado();
      
      // Cerrar el modal directamente sin usar callbacks complejos
      this.cerrar.emit();
      
      // Mostrar toast de éxito después de cerrar el modal
      setTimeout(() => {
        this.alertService.showSuccessToast(mensaje, 'Procesamiento Completado');
      }, 100);
      
    } else if (exitosos === 0) {
      const mensaje = `Ningún archivo se pudo procesar. ${errores} errores.`;
    } else {
      const mensaje = `Procesamiento completado: ${exitosos} exitosos, ${errores} errores.`;
      this.mostrarAlerta.emit({
        type: 'warning',
        title: 'Procesamiento Parcial',
        message: mensaje,
        confirmText: 'Aceptar',
        showCancel: false
      });
    }
  }
}
